































































































import { api, dashboardApi } from "@/api/api";
import { ApiGetDashboardDto, ApiGetEmployeeDto, ApiGetCurrentEmployeeDto } from "@/api/generated/Api";
import { AuthRole } from "@/auth/auth.constants";
import { authService } from "@/auth/authService";
import EmployeeSearchInput from "@/components/course/new/steps/info/EmployeeSearchInput.vue";
import BaseIconCard from "@/components/shared/BaseIconCard.vue";
import BaseLayout from "@/components/shared/BaseLayout.vue";
import { CourseResponsibleFilter } from "@/shared/enums/courseResponsibleFilter.enum";
import { GrantApplicationStatus } from "@/shared/enums/GrantApplicationStatus.enum";
import { LoadingType } from "@/shared/enums/loading-type.enum";
import { CourseRouteNames } from "@/shared/enums/RouteNames/courseRouteNames.enum";
import { DashboardRouteNames } from "@/shared/enums/RouteNames/dashboardRouteNames.enum";
import { hasMemberOrgAccess } from "@/shared/helpers/accessLevelApiAdapter";
import { globalLoadingWrapper } from "@/shared/helpers/loadingHelpers";
import { defineComponent, onMounted, ref } from "@vue/composition-api";
import { getDashboardUserId, getDashboardHttpClient, getCurrentUser } from "@/shared/helpers/dashboardHelpers";
import { DashboardUser } from "@/shared/constants/dashboardUser";
import GuestUserDashboardPage from "@/components/dashboard/GuestUserDashboardPage.vue";

export default defineComponent({
  name: "DashboardPage",
  components: { BaseIconCard, BaseLayout, EmployeeSearchInput, GuestUserDashboardPage },
  setup() {
    const dashboardApiData = ref<ApiGetDashboardDto>();
    const unreadMessages = ref("0");
    const grantRequests = ref();
    const employees = ref<ApiGetEmployeeDto[]>();
    const currentEmployeeId = ref<number>();
    const currentUser = ref<ApiGetCurrentEmployeeDto>();

    const isFilterOpen = ref(true);
    const isExtendedCourseAdmin = authService.hasRoles(AuthRole.ModifyExtendedCourse);

    onMounted(async () => {
      if (hasMemberOrgAccess) {
        return;
      }
      globalLoadingWrapper({ type: LoadingType.SkeletonTable }, async () => {
        await loadEmployees();
        currentUser.value = await getCurrentUser();
        currentEmployeeId.value = await getDashboardUserId();
        updateCurrentDashboardUser(currentEmployeeId.value);
        await Promise.all([loadDashboardApi(), loadUnreadMessagesCount(), getGrantApplications()]);
      });
    });

    const updateCurrentDashboardUser = (userId: number) => {
      const currentEmployeeOrgId = getEmployeeOrganizationId(userId);
      currentEmployeeId.value = userId;
      window.sessionStorage.setItem(DashboardUser.USER_ID, userId.toString());
      window.sessionStorage.setItem(DashboardUser.USER_ORG_ID, currentEmployeeOrgId.toString());
      dashboardApi.instance = getDashboardHttpClient(currentUser.value?.organizationId?.toString() ?? "");
    };

    const onSelectEmployee = async (employeeId?: number) => {
      if (employeeId === undefined) {
        return;
      }

      updateCurrentDashboardUser(employeeId);
      await loadDashboardApi();
      await loadUnreadMessagesCount();
    };

    const loadDashboardApi = async () => {
      globalLoadingWrapper({ blocking: true }, async () => {
        dashboardApiData.value = (
          await api.dashboard.getDashboardAsync({
            userId: currentEmployeeId.value,
            courseResponsibleFilter: CourseResponsibleFilter.SupervisorOrLecturer,
            getCurriculumForApprovalCount: true,
          })
        ).data;
      });
    };

    const getGrantApplications = async () => {
      if (!isExtendedCourseAdmin) {
        return;
      }
      const response = (await dashboardApi.course.getFilteredGrantApplicationsAsync()).data;
      if (!response?.length) {
        return;
      }
      grantRequests.value = response.filter(
        (grantApplication) => (grantApplication.applicationStatus = GrantApplicationStatus.SentForApproval)
      ).length;
    };

    const loadUnreadMessagesCount = async () => {
      const response = (
        await dashboardApi.messaging.searchMessagesKursadmin({
          CourseResponsibleFilter: CourseResponsibleFilter.SupervisorOrLecturer,
          UserId: currentEmployeeId.value,
          IsRead: false,
          Sender: false,
        })
      ).data.items;
      if (response) {
        unreadMessages.value = response.length.toString();
      }
    };

    const loadEmployees = async () => {
      employees.value = (await api.user.getEmployeesAsync()).data;
    };

    const getEmployeeOrganizationId = (employeeId: number) =>
      employees.value?.find((employee) => employee.id === employeeId)?.organizationId ?? 0;

    return {
      isExtendedCourseAdmin,
      isFilterOpen,
      unreadMessages,
      grantRequests,
      dashboardApiData,
      DashboardRouteNames,
      CourseRouteNames,
      hasMemberOrgAccess,
      currentEmployeeId,
      onSelectEmployee,
      firstName: authService.user.given_name,
    };
  },
});
